/**
 * Notification Context
 * Provides notification management across the application
 */

import React, { createContext, useState, useCallback, ReactNode } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
  action?: string;
  onAction?: () => void;
}

export interface NotificationContextType {
  notifications: Notification[];
  notify: (options: Omit<Notification, 'id'>) => string;
  success: (message: string, duration?: number) => string;
  error: (message: string, duration?: number) => string;
  warning: (message: string, duration?: number) => string;
  info: (message: string, duration?: number) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const NotificationContext = createContext<
  NotificationContextType | undefined
>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const notify = useCallback((options: Omit<Notification, 'id'>): string => {
    if (!options.message) throw new Error('Message is required');
    if (!options.type) throw new Error('Type is required');

    const id = `notification-${Date.now()}-${Math.random()}`;
    const notification: Notification = {
      id,
      ...options,
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-dismiss if duration is specified
    if (
      options.duration !== undefined &&
      options.duration !== 0 &&
      options.duration > 0
    ) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, options.duration);
    }

    return id;
  }, []);

  const success = useCallback(
    (message: string, duration?: number): string => {
      return notify({ type: 'success', message, duration });
    },
    [notify]
  );

  const error = useCallback(
    (message: string, duration?: number): string => {
      return notify({ type: 'error', message, duration });
    },
    [notify]
  );

  const warning = useCallback(
    (message: string, duration?: number): string => {
      return notify({ type: 'warning', message, duration });
    },
    [notify]
  );

  const info = useCallback(
    (message: string, duration?: number): string => {
      return notify({ type: 'info', message, duration });
    },
    [notify]
  );

  const dismiss = useCallback((id: string): void => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clear = useCallback((): void => {
    setNotifications([]);
  }, []);

  const value: NotificationContextType = {
    notifications,
    notify,
    success,
    error,
    warning,
    info,
    dismiss,
    clear,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = React.useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }
  return context;
};

export default NotificationProvider;
